import React, { useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Pricing.css';
import { LandingNav } from '../components/Nav';
import { COPY, HERO_CHAPTER_PILLS } from '../data';
import PricingPlansCore from '../components/PricingPlansCore';
import CommunitySection from '../components/CommunitySection';
import ChaptersSection from '../components/ChaptersSection';
import FeaturesSection from '../components/FeaturesSection';
import WhySection from '../components/WhySection';
import NovaSection from '../components/NovaSection';
import PrivacySection from '../components/PrivacySection';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useHowItWorksDesktopModal } from '../context/HowItWorksModalContext';
import { FERTILITY_DEMO_HOME } from '../constants/paths';
import { FooterNavLinks } from '../components/FooterNavLinks';
import LogoWithBeta from '../components/LogoWithBeta';

const s = {
  page:        { background:'var(--white)', minHeight:'100vh' },
  hero:        { display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', padding:'72px 40px 0', position:'relative', overflow:'hidden' },
  blob:        { position:'absolute', top:-80, left:'50%', transform:'translateX(-50%)', width:700, height:500, background:'radial-gradient(ellipse,#FBEAF0 0%,#E1F5EE 50%,transparent 72%)', opacity:.5, zIndex:0, pointerEvents:'none' },
  eyebrow:     { fontSize:11, fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--pink)', background:'var(--pink-l)', padding:'5px 16px', borderRadius:999, marginBottom:20, display:'inline-block', position:'relative', zIndex:1 },
  h1:          { fontFamily:'Playfair Display,serif', fontSize:48, lineHeight:1.18, color:'var(--text)', maxWidth:640, marginBottom:20, marginLeft:'auto', marginRight:'auto', letterSpacing:'-.5px', position:'relative', zIndex:1 },
  // Slightly wider so it naturally wraps to ~2 lines without forced breaks
  heroP:       { fontSize:15, color:'var(--muted)', maxWidth:520, lineHeight:1.75, marginBottom:14, marginLeft:'auto', marginRight:'auto', position:'relative', zIndex:1 },
  heroBtns:    { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', marginTop:26, marginBottom:0, position:'relative', zIndex:1 },
  mainBtn:     { fontSize:15, fontWeight:700, color:'white', background:'var(--pink)', border:'none', cursor:'pointer', padding:'14px 32px', borderRadius:999, boxShadow:'none' },
  secBtn:      { fontSize:14, fontWeight:600, color:'var(--pink-d)', background:'var(--pink-l)', border:'1.5px solid var(--pink-m)', cursor:'pointer', padding:'13px 26px', borderRadius:999 },
  // Make these feel like navigation labels (not CTA buttons)
  tabs:        { display:'flex', gap:18, alignItems:'center', justifyContent:'center', width:'fit-content', margin:'0 auto', position:'relative', zIndex:1 },
  tab:         { fontSize:12, fontWeight:600, padding:'6px 2px', border:'none', background:'transparent', cursor:'default', color:'var(--pink)', whiteSpace:'nowrap', opacity:.9 },
  // Full-width CTA band above footer (gradient bar)
  ctaWrap:     { background:'linear-gradient(140deg,var(--pink-l),var(--sage-l))', borderTop:'2.5px solid var(--pink-m)', borderBottom:'2.5px solid var(--pink-m)', padding:'46px 48px', textAlign:'center', width:'100%' },
  ctaInner:    { maxWidth:860, margin:'0 auto' },
};

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const m = useMediaQuery('(max-width: 767px)');
  const compactNav = useMediaQuery('(max-width: 900px)');
  const { openDesktopOnlyModal } = useHowItWorksDesktopModal();
  const sectionPad = { padding: m ? '48px 18px' : '64px 48px' };

  useEffect(() => {
    if (location.hash === '#privacy') {
      requestAnimationFrame(() => {
        document.getElementById('privacy')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.pathname, location.hash]);

  // ~2in shorter hero band vs prior minHeights (560 / 620).
  const heroBandShrinkPx = 192;
  const heroMinHeight = m ? 560 - heroBandShrinkPx : 620 - heroBandShrinkPx;

  const heroImgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: m ? 'center 0%' : 'center 0%',
    // Same anchor as before; nudge down so more of the heads read in the shorter band.
    transform: m ? 'translateY(11%) scale(1.04)' : 'translateY(10%) scale(1.06)',
    transformOrigin: 'center top',
  };

  return (
    <div style={s.page}>
      {/* One visual block: photo + overlay behind nav and hero copy */}
      <div style={{ position: 'relative', width: '100%', marginTop: 0, overflow: 'hidden' }}>
        <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <img src="/images/hero-women.png" alt="" style={heroImgStyle} />
        </div>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: 'linear-gradient(135deg,rgba(251,234,240,0.82) 0%,rgba(225,245,238,0.76) 100%)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <LandingNav />

          {/* HERO — copy sits in same visual stack as image */}
          <section
            style={{
              ...s.hero,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              padding: m ? '36px 18px 40px' : '48px 40px 48px',
              minHeight: heroMinHeight,
              width: '100%',
              boxSizing: 'border-box',
              background: 'transparent',
            }}
          >
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 720,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
        <div style={s.blob}></div>
        <div style={{ ...s.eyebrow, fontSize: m ? 10 : 11, padding: m ? '5px 12px' : '5px 16px', textAlign: 'center', maxWidth: '100%' }}>
          {COPY.tagline}
        </div>
        <h1
          style={{
            ...s.h1,
            fontSize: m ? 'clamp(26px, 7.5vw, 36px)' : 48,
            padding: m ? '0 4px' : 0,
            marginBottom: m ? 14 : 20,
          }}
          className="serif"
        >
          {COPY.heroHeadline}
          {COPY.heroHeadlineItalic ? (
            <>
              <br />
              <em style={{ fontStyle: 'italic', color: 'var(--pink)' }}>{COPY.heroHeadlineItalic}</em>
            </>
          ) : null}
        </h1>
        <p
          style={{
            ...s.heroP,
            fontSize: m ? 14 : 15,
            maxWidth: m ? '100%' : 520,
            padding: m ? '0 2px' : 0,
            marginBottom: m ? 12 : 14,
          }}
        >
          {COPY.heroSubtitleLead}
          {COPY.heroSubtitleRest ? (
            <>
              <br />
              {COPY.heroSubtitleRest}
            </>
          ) : null}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto 8px',
            padding: '0 12px 6px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            position: 'relative',
            zIndex: 1,
            boxSizing: 'border-box',
          }}
        >
          {HERO_CHAPTER_PILLS.map((c, i) => (
            <React.Fragment key={c.label}>
              {i > 0 && (
                <span
                  style={{ color: 'var(--pink-m)', fontSize: m ? 12 : 14, fontWeight: 600, userSelect: 'none', lineHeight: 1, flexShrink: 0, padding: '0 2px' }}
                  aria-hidden
                >
                  ·
                </span>
              )}
              <span
                style={{
                  ...s.tab,
                  fontSize: m ? 12 : 14,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: m ? 5 : 8,
                  padding: m ? '6px 4px' : '8px 6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: m ? 16 : 18, lineHeight: 1 }} aria-hidden>{c.icon}</span>
                <span>{c.label}</span>
              </span>
            </React.Fragment>
          ))}
        </div>

        <div
          style={{
            ...s.heroBtns,
            marginTop: m ? 14 : 20,
            marginBottom: m ? 8 : 12,
            flexDirection: m ? 'column' : 'row',
            width: m ? '100%' : 'auto',
            maxWidth: m ? 360 : 'none',
            padding: m ? '0 8px' : 0,
          }}
        >
          <button
            style={{ ...s.mainBtn, width: m ? '100%' : 'auto', boxSizing: 'border-box' }}
            onClick={() => navigate('/signup?source=landing-hero')}
          >
            {COPY.heroCta}
          </button>
          <button
            style={{ ...s.secBtn, width: m ? '100%' : 'auto', boxSizing: 'border-box' }}
            onClick={() => {
              if (compactNav) {
                openDesktopOnlyModal();
              } else {
                navigate(FERTILITY_DEMO_HOME);
              }
            }}
          >
            {COPY.heroCtaSecondary}
          </button>
        </div>

        </div>
          </section>
        </div>
      </div>

      <ChaptersSection />

      <FeaturesSection />

      <NovaSection />

      <PrivacySection />

      <CommunitySection />
      <WhySection />

      {/* PRICING — same plans & copy as /pricing */}
      <section
        className="pricing-section-band"
        style={{ ...sectionPad, scrollMarginTop: 88, backgroundColor: 'rgb(249, 246, 241)' }}
        id="pricing"
      >
        <PricingPlansCore embedded showTrustBar showFaq={false} bloomWaitlistSource="landing-pricing" />
      </section>

      {/* CTA — founder closing (homepage only) */}
      <section style={{ padding: 0 }}>
        <div style={{ ...s.ctaWrap, padding: m ? '40px 20px' : s.ctaWrap.padding }}>
          <div style={{ ...s.ctaInner, padding: m ? '0 4px' : 0 }}>
            <div className="closing-founder-section">
              <div className="quote-container">
                <p className="closing-quote">
                  &ldquo;I built OrivonAI because I needed it and it didn&apos;t exist. Seven years. Six losses. Four rounds of IVF.
                  One surrogate. Two children. You deserve a companion for every step of this.&rdquo;
                </p>
              </div>

              <div className="closing-attribution">
                <img
                  src="https://www.orivonai.com/images/blog/vanessa-profile.png"
                  alt="Vanessa Mosbacker"
                  className="closing-avatar"
                />
                <div>
                  <div className="closing-name">Vanessa Mosbacker</div>
                  <div className="closing-role">Founder &amp; CEO, OrivonAI</div>
                </div>
              </div>

              <button type="button" className="btn-primary closing-cta" onClick={() => navigate('/signup?source=landing-footer')}>
                Join the waitlist
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          padding: m ? '28px 18px' : '36px 48px',
          borderTop: '2.5px solid var(--pink-m)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 14,
          flexDirection: m ? 'column' : 'row',
          textAlign: m ? 'center' : 'left',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: m ? 'center' : 'flex-start' }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <LogoWithBeta compact={compactNav} />
          </Link>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>© 2026 OrivonAI · {COPY.footerTagline}</span>
        </div>

        <FooterNavLinks />
      </footer>
    </div>
  );
}
